import { MathUtil } from "@base/math/MathUtil";
import { AdditionalArticleInfos, findArticles, GuideNotchSide, IArticleAffiliation, OfferListType } from "@biz/definitions/ArticleDefinitions";
import { IArticleFeatureTemplate } from "@biz/definitions/ArticleFeatureDefinitions";
import { appTemplates, articleFeatureHandlers } from "@biz/definitions/ArticleShopTemplates";
import { ConfiguratorMode } from "@biz/definitions/IVariantData3d";
import { CalcProductLogic } from "@biz/items/CalcProductLogic";
import { getTieBackArticleNumber } from "@biz/items/curtain/CurtainData";
import { CurtainTieBackType, CurtainType } from "@biz/items/curtain/CurtainDefinitions";
import { CurtainLogic, ICalculatedClothInfo, ICalculatedGuideInfo, ICalculatedRopeInfo } from "@biz/items/curtain/CurtainLogic";
import { LocaContext } from "@biz/language/LocaContext";
import { ClothData } from "@biz/logic/ClothData";
import { IShoppingArticleOptions, PartListPriority } from "@biz/models/ShoppingListDefinitions";
import { ICalcContext, ProjectCheckErrorType } from "@biz/projectCalculation/ProjectDefinitions";

export class CalcCurtainLogic {
  static addGuideInfoArticles(slc: ICalcContext, guideInfo: ICalculatedGuideInfo): void {
    const guideCount = guideInfo.count;

    if (guideInfo.type === "slide") {
      const notchSide = (guideInfo.notchStart ? "L" : "") + (guideInfo.notchEnd ? "R" : "") as GuideNotchSide;

      CalcProductLogic.addRuleArticle(slc, guideInfo.articleNumber, {
        count: guideCount, mul: guideInfo.length, stkUnit: "Stk", mulUnit: "lfm", articleType: "obligatory", partList: { list: "none" },
        featureTemplate: appTemplates.curtainGuide,
        features: { length: guideInfo.length, notchSide, angleLeft: guideInfo.angleLeft, angleRight: guideInfo.angleRight },
        articleGroup: "curtainSlide",
        beosOrder: {
          anzahlCalculationType: "laufMeter",
          einheit: "Stk"
        }
      });


      if (CurtainLogic.isMitterAngle(guideInfo.angleLeft))
        CalcProductLogic.addRuleArticle(slc, "00E13001938", { count: guideCount, articleType: "obligatory", articleGroup: "curtainSlide", stkUnit: "Stk" });
      if (CurtainLogic.isMitterAngle(guideInfo.angleRight))
        CalcProductLogic.addRuleArticle(slc, "00E13001938", { count: guideCount, articleType: "obligatory", articleGroup: "curtainSlide", stkUnit: "Stk" });

      if (guideInfo.notchStart)
        CalcProductLogic.addRuleArticle(slc, guideInfo.notchStart, { count: guideCount, articleType: "obligatory", articleGroup: "curtainSlide" });
      if (guideInfo.notchEnd)
        CalcProductLogic.addRuleArticle(slc, guideInfo.notchEnd, { count: guideCount, articleType: "obligatory", articleGroup: "curtainSlide" });
    } else if (guideInfo.type === "arc") {
      CalcProductLogic.addRuleArticle(slc, guideInfo.articleNumber, { count: guideCount, articleType: "obligatory", partList: { list: "none" }, articleGroup: "curtainArc" });
    }
  }

  static calcCurtainErrors(slc: ICalcContext): void {
    if (findArticles(slc.ruleArticles, ["00E13000758", "00E13001938"]).count > 0 && findArticles(slc.ruleArticles, ["00E13001774"]).count === 0) { // https://plaspack.atlassian.net/browse/APP-1233
      CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Hint, "product", slc.lc.t("Rule_Courtain_ArcOrMitter"));
    }
  }

  static addCurtainMountArticles(slc: ICalcContext, curtainType: CurtainType, mode: ConfiguratorMode, options: Partial<IShoppingArticleOptions>): void {
    switch (curtainType) {
      case "guide": CalcCurtainLogic.addCurtainGuideArticles(slc, mode, { ...options, quickCategory: "curtainTracks" }); break;
      case "rope": CalcCurtainLogic.addCurtainRopeArticles(slc, mode, { ...options, quickCategory: "curtainRopes" }); break;
      case "clothOnly": {
        break;
      }
    }
  }

  static addCurtainGuideArticles(slc: ICalcContext, mode: "2d" | "3d", options: Partial<IShoppingArticleOptions>): void {

    if (mode === "2d")
      CalcProductLogic.addRuleArticle(slc, "00E13000758", { count: 0, articleType: "optional", quickCategory: "curtainMount", ...options }) // Arc

    CalcProductLogic.addRuleArticle(slc, "00E13001214", { count: 0, articleType: "optional", quickCategory: "curtainMount", ...options }) // Verbinder
    CalcProductLogic.addRuleArticle(slc, "00E13001107", { count: 2, articleType: "optional", quickCategory: "curtainMount", ...options }) // Stopper Führungsschiene
    CalcProductLogic.addRuleArticle(slc, "00E13001109", { count: 0, articleType: "optional", quickCategory: "curtainMount", ...options }) // Bohrschablone
    CalcProductLogic.addRuleArticle(slc, "00E13001419", { count: 0, articleType: "optional", quickCategory: "curtainMount", ...options }) // Winkelliste aus Aluminium eloxiert
  }

  static addCurtainRopeArticles(slc: ICalcContext, mode: ConfiguratorMode, options: Partial<IShoppingArticleOptions>): void {
    if (mode === "2d") {
      CalcProductLogic.addRuleArticle(slc, "00E13000113", { count: 0, articleType: "optional", quickCategory: "curtainMount", ...options }) // Verschraubbares Augterminal
      CalcProductLogic.addRuleArticle(slc, "00E13000126", { count: 0, articleType: "optional", quickCategory: "curtainMount", ...options }) // Gewindespanner M8
      CalcProductLogic.addRuleArticle(slc, "00E13000130", { count: 0, articleType: "optional", quickCategory: "curtainMount", ...options }) // Gewindespanner M10
    }

    CalcProductLogic.addRuleArticle(slc, "00E13000664", { count: 0, articleType: "optional", quickCategory: "curtainMount", ...options }) // Drahtseilklemme aus Edelstahl
    CalcProductLogic.addRuleArticle(slc, "00E13000066", { count: 0, articleType: "optional", quickCategory: "curtainMount", ...options }) // Ringbolzen aus Edelstahl 80mm
    CalcProductLogic.addRuleArticle(slc, "00E13000063", { count: 0, articleType: "optional", quickCategory: "curtainMount", ...options }) // Ringschraube Gewinde 8mm aus Edelstahl
    // CalcSailLogic.addRuleArticle(slc, "00E13001224", { count: 0, articleType: "optional", quickCategory: "curtainMount", ...options }) // Karabiner //( https://plaspack.atlassian.net/jira/software/projects/APP22/boards/2?selectedIssue=APP22-959
  }

  static #calcClothAdditionalArticleInfos(clothInfo: ICalculatedClothInfo, slc: ICalcContext): AdditionalArticleInfos {
    const { lc } = slc;
    const ret: AdditionalArticleInfos = {};
    // ret.color = AdditionalArticleInfo.colorId(slc, clothInfo.colorId);
    // ret.area = { header: lc.t("ArticleInfo_Area"), text: CurtainLogic.calcClothArea(clothInfo.finalClothWidthMid, clothInfo.height, clothInfo.height2).toFixed(1) + Measure.m2 };
    ret.shape = { header: lc.t("ArticleInfo_Shape"), text: lc.t(LocaContext.connect("Curtain_Shape", clothInfo.shape)) };
    ret.width = { header: lc.t("ArticleInfo_Width"), text: CurtainLogic.getWidthTexts(clothInfo.width, clothInfo.additionFactor) };
    if (CurtainLogic.isSlopingShape(clothInfo.shape))
      ret.widthSlop = { header: lc.t("ArticleInfo_Width_Slope"), text: CurtainLogic.getWidthTexts(clothInfo.width2, clothInfo.additionFactor) }
    ret.height = { header: lc.t("ArticleInfo_Height"), text: CurtainLogic.getHeightTexts(clothInfo) };
    ret.confectionTop = { header: lc.t("ArticleInfo_Confection_Top"), text: lc.t(LocaContext.connect("Curtain_Confection", clothInfo.confectionType)) };
    ret.confectionBottom = { header: lc.t("ArticleInfo_Confection_Bottom"), text: lc.t(LocaContext.connect("Curtain_ConfectionBottom", clothInfo.confectionBottomType)) }
    ret.suspension = { header: lc.t("ArticleInfo_Suspension"), text: lc.t(LocaContext.connect("Curtain_Suspension", clothInfo.suspensionType)) }
    return ret
  }

  static calcClothArticlesFromClothInfos(slc: ICalcContext, clothInfos: ICalculatedClothInfo[]): void {
    const offerList: OfferListType | undefined = undefined; // "S"; => allInOne two beosOrders are created...

    for (const ci of clothInfos) {

      if (!ci.cloth)
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "net", "Invalid cloth!");
      else {
        if (CurtainLogic.isSlopingShape(ci.shape) && ci.width > ci.width2)
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "net", "Länge Schräge muss >= Vorhangbreite sein!").addFieldsDirect<any>("form", ["Cloth", ci.trackIndex, ci.clothIndex, "width2"].join("_"));

        const baseArticleId = ClothData.getBaseArticleId(ci.cloth)
        const sunSailArea = CurtainLogic.calcClothArea(ci.finalClothWidthMid, ci.height, ci.height2);

        const additionalArticleInfo = CalcCurtainLogic.#calcClothAdditionalArticleInfos(ci, slc);
        const curtainClothFeatureTemplate: IArticleFeatureTemplate = {
          colorId: { ...articleFeatureHandlers.colorId, beosVariants: (val) => (val ? { "41": "" + val } : undefined) },
          area: { ...articleFeatureHandlers.areaInM2, mul: true },
        }
        CalcProductLogic.addRuleArticle(slc, baseArticleId, {
          featureTemplate: curtainClothFeatureTemplate,
          features: {
            colorId: ci.colorId,
            curtainShapeType: ci.shape,
            curtainWidth: ci.width,
            curtainAdditionFactor: ci.additionFactor,
            curtainWidth2: ci.width2,
            curtainHeight: ci.height,
            curtainHeight2: ci.height2,
            curtainConfectionType: ci.confectionType,
            curtainConfectionBottomType: ci.confectionBottomType,
            curtainSuspensionType: ci.suspensionType
          },
          additionalArticleInfo,
          count: ci.count, mul: sunSailArea,
          affiliation: { trackIndex: ci.trackIndex, clothIndex: ci.clothIndex },
          partList: { list: "none", priority: PartListPriority.sail },
          offerList,
          stkUnit: "Stk", mulUnit: "m2",
          articleGroup: "curtainCloth",
          beosOrder: {
            anzahlCalculationType: "quadratMeter",
          },
        });

        if (CurtainLogic.isSlopingShape(ci.shape)) {
          if (ci.additionFactor > 0 && ci.width2 !== undefined && ci.width !== ci.width2) {
            const length = Math.ceil(ci.width2 + 0.2)
            CalcProductLogic.addRuleArticle(slc, "00E13001770", { count: 1, articleType: "obligatory", mul: length, stkUnit: "Stk", mulUnit: "lfm", articleGroup: "curtainCloth" })
          }
        }

        CalcCurtainLogic.addTieBackArticle(slc, ci.count * ci.tieBackCount, ci.tieBackType, ci.tieBackLength, ci.cloth.articleId.group, ci.colorId, { trackIndex: ci.trackIndex, clothIndex: ci.clothIndex });
      }
    }
  }

  static addTieBackArticle(slc: ICalcContext, count: number, tieBackType: CurtainTieBackType | undefined, tieBackLength: number, clothGroup: string, colorId: string, affiliation?: IArticleAffiliation): void {
    if (tieBackType && count > 0) {
      const articleNumber = getTieBackArticleNumber(tieBackType, clothGroup);
      const length = MathUtil.round(tieBackLength, 2);
      CalcProductLogic.addRuleArticle(slc, articleNumber, {
        features: {
          tieBackLength: length,
          colorId,
        },
        featureTemplate: {
          tieBackLength: articleFeatureHandlers.tieBackLength,
          colorId: articleFeatureHandlers.colorId
        },
        count,
        affiliation: affiliation,
        articleType: "obligatory",
        stkUnit: "Stk",
        articleGroup: "curtainCloth",
        partList: {
          list: "sunSailList"
        }
      });
    }
  }

  static addCurtainRope(slc: ICalcContext, rope: ICalculatedRopeInfo, options?: Partial<IShoppingArticleOptions>): void {
    CalcProductLogic.addDrahtseil(slc, rope.length, 4, { count: rope.count, startTerminal: rope.startTerminal, endTerminal: rope.endTerminal }, options);
  }

  static addCurtainClothAdditionalArticles(slc: ICalcContext, curtainType: CurtainType, _mode: ConfiguratorMode, options: Partial<IShoppingArticleOptions>): void {
    CalcProductLogic.addRuleArticle(slc, "00E13001776", options); // Wind-Stopper für Outdoorvorhang mit Solidaylogo
    CalcProductLogic.addRuleArticle(slc, "00E13001813", options); // Wind-Stopper für Outdoorvorhang Pyramide - 2kg inkl. Spannquick
    CalcProductLogic.addRuleArticle(slc, "00E13001845", options); // Fugenanker
    CalcProductLogic.addRuleArticle(slc, "00E13001912", options); // Stirnplatte 45x15mm A2
    CalcProductLogic.addRuleArticle(slc, "00E13001914", options); // Klappbare Deckplatte aus Edelstahl
    CalcProductLogic.addRuleArticle(slc, "00710A002", options); //
    CalcProductLogic.addRuleArticle(slc, "00710A003", options); //
    CalcProductLogic.addRuleArticle(slc, "00710A004", options); //
    CalcProductLogic.addRuleArticle(slc, "00710A005", options); //
    CalcProductLogic.addRuleArticle(slc, "00E13000016", options); //
    CalcProductLogic.addRuleArticle(slc, "00E13000258", options); //
    CalcProductLogic.addRuleArticle(slc, "00E13000012", options); // Spannflex schwarz

    if (curtainType === "guide") {
      CalcProductLogic.addRuleArticle(slc, "00E13001774", options); // https://plaspack.atlassian.net/jira/software/projects/APP/issues/APP-1282
      CalcProductLogic.addRuleArticle(slc, "00E13001300", options); // https://plaspack.atlassian.net/jira/software/projects/APP/issues/APP-1282
      CalcProductLogic.addRuleArticle(slc, "00E13001715", options); // https://plaspack.atlassian.net/jira/software/projects/APP/issues/APP-1282
    }
  }
}